import * as React from 'react';
import type { MenuItemProps, SelectTabEvent } from '@fluentui/react-components';
import {
  getIntrinsicElementProps,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MenuButton,
  slot,
  useOverflowMenu,
  useIsOverflowItemVisible,
  useTabListContext_unstable,
  MenuItem,
} from '@fluentui/react-components';
import type {
  GroundingCommandBarOverflowMenuProps,
  GroundingCommandBarOverflowMenuState,
} from './GroundingCommandBarOverflowMenu.types';
import type { GroundingCommandBarTabProps } from '../GroundingCommandBarTab';
import { useGroundingCommandBarTabValue } from '../../utils';

type OverflowItem = {
  value: GroundingCommandBarTabProps['value'];
  children?: React.ReactNode;
  onClick: MenuItemProps['onClick'];
};
const OverflowMenuItem = (props: OverflowItem) => {
  const { children, onClick } = props;
  const value = useGroundingCommandBarTabValue(props.value);
  const isVisible = useIsOverflowItemVisible(value);
  if (isVisible) {
    return null;
  }
  return <MenuItem onClick={onClick}>{children}</MenuItem>;
};

/**
 * Create the state required to render GroundingCommandBarOverflowMenu.
 *
 * The returned state can be modified with hooks such as useGroundingCommandBarOverflowMenuStyles_unstable,
 * before being passed to renderGroundingCommandBarOverflowMenu_unstable.
 *
 * @param props - props from this instance of GroundingCommandBarOverflowMenu
 * @param ref - reference to root HTMLElement of GroundingCommandBarOverflowMenu
 */
export const useGroundingCommandBarOverflowMenu_unstable = (
  props: GroundingCommandBarOverflowMenuProps,
  ref: React.Ref<HTMLSpanElement>,
): GroundingCommandBarOverflowMenuState => {
  const { children, onTabSelect, overflowButtonLabel, ...rest } = props;

  const { ref: overflowRef, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

  const onItemClick = React.useCallback(
    (e: SelectTabEvent, tabValue: unknown) => {
      onTabSelect?.(e, { value: tabValue });
    },
    [onTabSelect],
  );

  const overflowItems = React.useMemo(
    () =>
      React.Children.map(children, child => {
        if (React.isValidElement<GroundingCommandBarTabProps>(child)) {
          return (
            <OverflowMenuItem
              {...child.props}
              onClick={e => onItemClick(e, child.props.value)}
              key={child.key?.toString()}
            />
          );
        } else {
          return undefined;
        }
      }),
    [children, onItemClick],
  );

  const registeredTabData = useTabListContext_unstable(ctx => ctx.getRegisteredTabs)();
  const selectedValue = useTabListContext_unstable(ctx => ctx.selectedValue);

  const baseOverflowButtonLabel = `${overflowCount} more`;
  const [defaultOverflowButtonLabel, setDefaultOverflowButtonLabel] = React.useState(baseOverflowButtonLabel);

  // Calculate label for overflow button
  React.useEffect(() => {
    const hasVisibleTabs = overflowCount < (overflowItems?.length ?? Number.MAX_SAFE_INTEGER);
    if (!selectedValue || hasVisibleTabs) {
      setDefaultOverflowButtonLabel(baseOverflowButtonLabel);
      return;
    }

    // If all tabs are overflowed, use the selected tab's label as the overflow button label
    const selectedLabel = registeredTabData.registeredTabs[JSON.stringify(selectedValue)]?.ref?.current?.innerText;
    setDefaultOverflowButtonLabel(selectedLabel ?? baseOverflowButtonLabel);
  }, [baseOverflowButtonLabel, overflowCount, overflowItems?.length, registeredTabData.registeredTabs, selectedValue]);

  const buttonLabel = overflowButtonLabel?.(overflowCount) ?? defaultOverflowButtonLabel;

  return {
    isOverflowing,
    components: {
      root: 'span',
      menuTrigger: MenuTrigger,
      menuButton: MenuButton,
      menuPopover: MenuPopover,
      menuList: MenuList,
    },
    root: slot.always(
      getIntrinsicElementProps('span', {
        ref,
        ...rest,
      }),
      { elementType: 'span' },
    ),
    menuTrigger: slot.optional(props.menuTrigger, { renderByDefault: true, elementType: MenuTrigger }),
    menuButton: slot.optional(props.menuButton, {
      defaultProps: { ref: overflowRef, appearance: 'subtle', shape: 'circular', children: buttonLabel, role: 'tab' },
      renderByDefault: true,
      elementType: MenuButton,
    }),
    menuPopover: slot.optional(props.menuPopover, { renderByDefault: true, elementType: MenuPopover }),
    menuList: slot.optional(props.menuList, {
      defaultProps: { children: overflowItems },
      renderByDefault: true,
      elementType: MenuList,
    }),
  };
};
